// COPIED AND MODIFIED FROM:
// https://github.com/rjsf-team/react-jsonschema-form/blob/f8378c48d7ef51bc47c1c150578189d5a613f2c5/packages/core/src/components/templates/ObjectFieldTemplate.tsx
import React from "react";
import {
  descriptionId,
  FormContextType,
  getTemplate,
  getUiOptions,
  ObjectFieldTemplatePropertyType,
  ObjectFieldTemplateProps,
  RJSFSchema,
  StrictRJSFSchema,
  TitleFieldProps,
  titleId,
} from "@rjsf/utils";
import { ContentWrapper, Group } from "./__styles__/ObjectFieldTemplate";
import { TitleFieldTemplateProps } from "./TitleFieldTemplate";

export const ObjectFieldTemplate = <
  T = any,
  S extends StrictRJSFSchema = RJSFSchema,
  F extends FormContextType = any,
>(
  props: ObjectFieldTemplateProps<T, S, F> & { isSubmissionBuilder?: boolean },
) => {
  const {
    idSchema,
    properties,
    registry,
    required,
    schema,
    title,
    uiSchema,
    isSubmissionBuilder,
  } = props;
  const options = getUiOptions<T, S, F>(uiSchema);

  // NOTE: We're type-casting here to allow passing the `isInGroup` prop
  // to the `TitleFieldTemplate` component.
  // As of writing, all headings in our form schemas are currently being
  // defaulted `h1`, which is semantically incorrect and looks too large when a
  // `TitleFieldTemplate` is rendered within a group.
  // In the long run, we could do one of the following:
  // 1. Set the correct heading level when creating new submissions (`h3` and
  //    lower for groups), and then potentially backfill old submission types.
  // 2. Use a `HeadingLevel` context to dynamically set heading levels, and leave
  //    heading levels out of the form schema entirely.
  const TitleFieldTemplate = getTemplate<"TitleFieldTemplate", T, S, F>(
    "TitleFieldTemplate",
    registry,
    options,
  ) as React.ComponentType<
    TitleFieldProps<T, S, F> & Pick<TitleFieldTemplateProps, "isInGroup">
  >;

  const DescriptionFieldTemplate = getTemplate<
    "DescriptionFieldTemplate",
    T,
    S,
    F
  >("DescriptionFieldTemplate", registry, options);

  const columns = uiSchema?.["ui:columns"];

  if (uiSchema?.["ui:widget"] === "hidden") {
    return null;
  }

  // NOTE(raph): For the `description` convenience prop, RJSF prioritizes the
  // ui:schema over the schema. Unfortunately, we are setting `ui:description`
  // to an empty string when saving forms (which overrides the schema description).
  // The following makes sure we prioritize the description on the schema, which
  // we is where we store the actual description.
  const description = schema.description || props.description;

  return (
    <Group id={idSchema.$id}>
      {title ?
        <TitleFieldTemplate
          id={titleId<T>(idSchema)}
          title={title}
          required={required}
          schema={schema}
          uiSchema={uiSchema}
          registry={registry}
          isInGroup
        />
      : null}
      {description ?
        <DescriptionFieldTemplate
          id={descriptionId<T>(idSchema)}
          description={description}
          schema={schema}
          uiSchema={uiSchema}
          registry={registry}
        />
      : null}
      <ContentWrapper
        isSubmissionBuilder={!!isSubmissionBuilder}
        hasColumns={columns != null}
      >
        {properties.map(
          (prop: ObjectFieldTemplatePropertyType) => prop.content,
        )}
      </ContentWrapper>
    </Group>
  );
};
